import { Formik, Form } from 'formik';
import styles from './EditRowModal.module.css';
import Modal from '../../../Modals/Modal';
import DynamicField from '../../../DynamicForm/DynamicField';
import { useLanguage } from '../../../../context/LanguageContext';
import { TableRow } from '../../../../types/types.table';
import {Field} from "../../../../types/types.form.ts";

export interface EditRowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: TableRow) => Promise<void>; 
  rowData: TableRow | null;
  columns: string[];
  columnTypes?: Record<string, string>;
    relationFields?: Record<string, string>;
}

const getFieldType = (value: any): 'text' | 'checkbox' => {
  if (typeof value === 'boolean') {
    return 'checkbox';
  }
  if (value === 'true' || value === 'false' || value === true || value === false) {
    return 'checkbox';
  }
  return 'text';
};

const normalizeValue = (value: any) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
};

export default function EditRowModal({
  isOpen,
  onClose,
  onSave,
  rowData,
  columns,
  columnTypes = {},
    relationFields = {},
}: EditRowModalProps) {
  const { t } = useLanguage();

  const initialValues: TableRow = rowData 
    ? Object.keys(rowData).reduce((acc, key) => {
        acc[key] = normalizeValue(rowData[key]);
        return acc;
      }, {} as TableRow)
    : columns
        .filter(col => col !== 'id')
        .reduce((acc, col) => {
          acc[col] = '';
          return acc;
        }, { id: 0 } as TableRow);

  const handleSubmit = async (values: TableRow, { setSubmitting }: any) => { 
    try {
      setSubmitting(true);
      const updatedRow = {
        ...values,
        id: Number(rowData?.id || values.id)
      };
      await onSave(updatedRow); 
    } catch (err) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('editRow')}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => ( 
          <Form className={styles.form}>
            <input type="hidden" name="id" value={rowData?.id || 0} />
            
            {columns
              .filter(col => col !== 'id')
              .map(column => {
                const SYSTEM_READONLY_FIELDS = ["id", "created_at", "updated_at"];
                const originalValue = rowData ? rowData[column] : '';
                const fieldType = columnTypes[column] || getFieldType(originalValue);
                const field = {
                  name: column,
                  type: fieldType,
                  relationModel: relationFields[column] || '',
                  label: column.charAt(0).toUpperCase() + column.slice(1),
                  disabled: SYSTEM_READONLY_FIELDS.includes(column)
                };
                return <DynamicField key={column} field={field as Field} />;
              })}

            <div className={styles.actions}>
              <button 
                type="button" 
                className={styles.cancelBtn} 
                onClick={onClose}
                disabled={isSubmitting} 
              >
                {t('cancel')}
              </button>
              <button 
                type="submit" 
                className={styles.saveBtn}
                disabled={isSubmitting} 
              >
                {isSubmitting ? t('saving') : t('saveChanges')} 
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}